import Phaser from 'phaser';
import { ScareConfig, GHOST_IDLE_TIMER } from '../consts/config';
import { spriteSize, tileSize } from '../consts/sizes';
import { GhostAI } from './ghost-ai/GhostAI';
import HouseAI from './ghost-ai/HouseAI';
import { blue, green, orange, pink, red } from './colors';

import { MoveMap, Moves, PerpMap } from './grid/grid';
import RespawnAI from './ghost-ai/RespawnAI';
import { boardCollision } from './colliders/board';

export enum GhostState {
	Idle,
	House,
	Normal,
	Scared,
	Respawning
}
export default class Ghost extends Phaser.GameObjects.Container {
	private readonly boardLayer: Phaser.Tilemaps.TilemapLayer;
	private useDoor: Phaser.GameObjects.Rectangle | null;
	private readonly door: Phaser.GameObjects.Rectangle;
	private readonly ghostBody: Phaser.GameObjects.Sprite;
	private readonly eyes: Phaser.GameObjects.Image;
	private readonly leftPupil: Phaser.GameObjects.Image;
	private readonly rightPupil: Phaser.GameObjects.Image;
	private readonly scaredEyes: Phaser.GameObjects.Image;
	private sensors!: Phaser.GameObjects.Rectangle[];

	private _aiBehavior?: GhostAI;
	private _defaultAI!: GhostAI;
	private _scareAI!: GhostAI;
	private _color!: Phaser.Display.Color;
	private _level = 0;
	private lastDirection = Moves.None;
	private nextSnap: { move: Moves, pos: Phaser.Math.Vector2 } | null = null;
	private snapFrameCounter = 0;

	private targetIndicator: Phaser.GameObjects.Text;

	private _ghostState = GhostState.Normal;
	private _blinkTimers: Phaser.Time.TimerEvent[] = [];
	private _aiModeTimer?: Phaser.Time.TimerEvent;

	private readonly colorSpriteMap = {
		[red.color]: 'enemy4',
		[green.color]: 'enemy1',
		[pink.color]: 'enemy3',
		[orange.color]: 'enemy2'
	};
	public allowedDirections = {
		[Moves.Up]: true,
		[Moves.Down]: true,
		[Moves.Left]: true,
		[Moves.Right]: true,
	};

	constructor(
		scene: Phaser.Scene,
		pos: Required<Phaser.Types.Math.Vector2Like>,
		color: Phaser.Display.Color,
		boardLayer: Phaser.Tilemaps.TilemapLayer,
		door: Phaser.GameObjects.Rectangle,
		level: number
	) {
		super(scene, pos.x, pos.y);

		this.boardLayer = boardLayer;
		this.door = door;
		this.useDoor = this.door;
		this._color = color;
		this._level = level;

		this.ghostBody = scene.add.sprite(0, 0, 'kebab_tiles');
		this.ghostBody.play(`${this.colorSpriteMap[this._color.color]}-idle`);
		this.ghostBody.setTint(this._color.color);

		this.eyes = scene.add.image(0, 0, 'kebab_tiles', 'ghost-eyes.png');

		this.leftPupil = scene.add.image(0, 0, 'kebab_tiles', 'ghost_pupil.png')
			.setTint(0x2F16AF);
		this.rightPupil = scene.add.image(0, 0, 'kebab_tiles', 'ghost_pupil.png')
			.setTint(0x2F16AF);

		this.scaredEyes = scene.add.image(0, 16, 'kebab_tiles', 'scared-eyes.png');
		this.scaredEyes.setVisible(false);

		this.add(this.ghostBody);
		this.add(this.eyes);
		this.add(this.leftPupil);
		this.add(this.rightPupil);
		this.add(this.scaredEyes);

		this.lookTo(this.currentDirection);
		this.setDepth(3);
		this.scene.physics.add.collider(this, this.boardLayer, () => {
			const { moves } = this.sense();
			if (moves.length > 0) {
				this.changeDirection(moves[0].move);
			}
		});

		this.sensors = [
			scene.add.rectangle(pos.x, pos.y, spriteSize, spriteSize, new Phaser.Display.Color(255, 0, 0).color, 0.8),
			scene.add.rectangle(pos.x, pos.y, spriteSize, spriteSize, new Phaser.Display.Color(0, 255, 0).color, 0.8)
		];
		this.sensors.forEach(s => s.setOrigin(0.5, 0.5).setVisible(!!scene.game.config.physics.arcade?.debug));

		this.targetIndicator = scene.add.text(0, 0, '❌')
			.setOrigin(0.5)
			.setDepth(1000)
			.setColor(color.rgba);

		this.targetIndicator.setVisible(scene.game.config.physics.arcade?.debug || false);

		const idleTime = {
			[red.color]: 0,
			[pink.color]: GHOST_IDLE_TIMER + 1000,
			[orange.color]: GHOST_IDLE_TIMER + 2000,
			[green.color]: GHOST_IDLE_TIMER + 3000,
		};
		this.scene.time.delayedCall(idleTime[color.color], () => {
			this.currentAI = new HouseAI(this);
			this.changeDirection(Moves.Left);
		});
	}

	lookTo(direction: Moves) {
		const len = spriteSize / tileSize / 2;
		const l = new Phaser.Math.Vector2(-7, 1).add(new Phaser.Math.Vector2(len).multiply(MoveMap[direction]));
		const r = new Phaser.Math.Vector2(l.x + 7, l.y).add(new Phaser.Math.Vector2(len * 4).multiply(MoveMap[Moves.Right]));

		this.leftPupil.x = l.x;
		this.leftPupil.y = l.y;
		this.rightPupil.x = r.x;
		this.rightPupil.y = r.y;
	}

	preUpdate() {
		if (!this.currentAI) {
			return;
		}
		this.currentAI.chooseTarget();
		if ([GhostState.Idle, GhostState.House].includes(this.ghostState)) {
			return;
		}

		if (this.nextSnap !== null && this.snapFrameCounter++ > 2) {
			// if (this.nextSnap !== null) {
			const { move, pos } = this.nextSnap;
			if (this.currentDirection !== move) {
				this.setPosition(pos.x, pos.y);
			}
			this.changeDirection(move);
			this.nextSnap = null;
			this.snapFrameCounter = 0;
		}

		this.scene.physics.world.wrapObject(this, spriteSize);

		if (!Phaser.Geom.Rectangle.Contains(this.scene.physics.world.bounds, this.x, this.y)) {
			return;
		}

		// Use sensors to see if Ghost has any options to turn
		const { collisionTilePos, moves } = this.sense(tileSize / 2 + 1);

		if (moves.length < 1) {
			// got no viable moves
			// collision events will make sure this doesn't stop the ghosts
			return;
		}

		const potentialDirections = moves.concat({
			move: this.currentDirection,
			pos: collisionTilePos.clone().add(new Phaser.Math.Vector2(tileSize * 2).multiply(MoveMap[this.currentDirection])),
			unobstructed: true
		});

		// shortest Move calculation
		const maxDist = 10000;
		const target = this.currentAI.targetPosition;
		const shortest = potentialDirections.reduce<{ move: Moves, dist: number }>(
			(acc, { pos, move }) => {
				const dist = new Phaser.Math.Vector2(target).distance(pos);
				return dist < acc.dist ? { move, dist } : acc;
			}, { move: Moves.None, dist: maxDist });

		this.nextSnap = {
			move: shortest.move,
			pos: collisionTilePos
		};

		if (this.scene.game.config.physics.arcade?.debug) {
			const { x: tx, y: ty } = this.currentAI.targetPosition;
			this.targetIndicator.setPosition(tx, ty);
		}
	}

	public changeDirection(move: Moves) {
		if (move === Moves.None || move === this.currentDirection) {
			return;
		}

		const { speed } = this.currentAI!;
		const dir = MoveMap[move];
		(this.body as Phaser.Physics.Arcade.Body)
			.setVelocity(dir.x * speed, dir.y * speed);

		this.lookTo(move);
		this.lastDirection = move;
	}

	private sense = (offset = 0) => {
		const sensorPos = new Phaser.Math.Vector2(this)
			.add(new Phaser.Math.Vector2(offset) // slightly in front of Ghost
				.multiply(MoveMap[this.currentDirection]));

		const collisionTilePos = new Phaser.Math.Vector2({ // round to closest tile
			x: Math.round(sensorPos.x / tileSize) * tileSize,
			y: Math.round(sensorPos.y / tileSize) * tileSize
		});

		const moves = PerpMap[this.currentDirection].map((perp, i) => {
			const sensor = this.sensors[i];
			const pos = collisionTilePos.clone().add(new Phaser.Math.Vector2(tileSize * 2)
				.multiply(MoveMap[perp]));

			sensor.setPosition(pos.x, pos.y);
			return { move: perp, pos, unobstructed: boardCollision(sensor.getBounds(), this.boardLayer, this.useDoor) };
		})
			.filter(({ unobstructed }) => unobstructed);

		return { collisionTilePos, moves };
	};

	private toggleScareVisuals(scared: boolean) {
		this.ghostBody.setTint(scared ? blue.color : this._color.color);
		this.eyes.setVisible(!scared);
		this.leftPupil.setVisible(!scared);
		this.rightPupil.setVisible(!scared);
		this.scaredEyes.setVisible(scared);
	}

	private scareBlink(blinkTime) {
		if (this.isScared) {
			this.ghostBody.setTint(this._color.color);

			this.scene.time.delayedCall(
				blinkTime / 2,
				() => {
					this.ghostBody.setTint(blue.color);
				}
			);
		}
	}

	private toggleAllowedMove(move: Moves, untoggleTime?: number) {
		this.allowedDirections[move] = !this.allowedDirections[move];
		if (untoggleTime) {
			this.scene.time.delayedCall(untoggleTime, () => this.toggleAllowedMove(move));
		}
	}

	public floorPosition() {
		this.setPosition(Math.floor(this.x), Math.floor(this.y));
	}

	public unscare() {
		if (this.isScared && !this.isRespawning) {
			this.ghostState = GhostState.Normal;
			this.toggleScareVisuals(false);
			this.currentAI = this.defaultAI;
		}
	}

	public scare({ time, numBlinks }: ScareConfig) {
		if (!(this.currentAI === this.defaultAI || this.currentAI === this.scareAI)) {
			return;
		}

		this.ghostState = GhostState.Scared;
		this.toggleScareVisuals(true);

		this.currentAI = this.scareAI;
		const blinkTime = 500;

		this._blinkTimers.forEach(timer => timer.remove());
		this._blinkTimers = [];
		for (let i = 1; i <= numBlinks; i++) {
			this._blinkTimers.push(this.scene.time.delayedCall(
				time - blinkTime * i,
				() => this.scareBlink(blinkTime)
			));
		}
	}

	public handleEaten() {
		if (this.isRespawning) {
			return;
		}

		this.scene.sound.playAudioSprite('sfx', `ghost ate`);

		this.ghostState = GhostState.Respawning;
		this.currentAI = new RespawnAI(this);
		this.ghostBody.setVisible(false);
		this.toggleScareVisuals(false);
		this.scaredEyes.setVisible(false);
		this.useDoor = null;
	}

	public respawn() {
		this.currentAI = new HouseAI(this);
		this.ghostBody.setVisible(true);
		this.toggleScareVisuals(false);
		this.useDoor = this.door;
	}

	public hasRespawned() {
		this.ghostState = GhostState.Normal;
		this.currentAI = this.defaultAI;
		this.changeDirection([Moves.Left, Moves.Right][Math.round(Math.random())]);
	}

	get defaultAI() {
		return this._defaultAI;
	}
	set defaultAI(defaultAI: GhostAI) {
		this._defaultAI = defaultAI;
	}

	get scareAI() {
		return this._scareAI;
	}
	set scareAI(scareAI: GhostAI) {
		this._scareAI = scareAI;
	}

	get currentAI() {
		return this._aiBehavior!;
	}
	set currentAI(ai: GhostAI) {
		this._aiBehavior = ai;
	}

	get isNormal() {
		return this._ghostState === GhostState.Normal;
	}
	get isScared() {
		return this._ghostState === GhostState.Scared;
	}
	get isRespawning() {
		return this._ghostState === GhostState.Respawning;
	}
	get ghostState() {
		return this._ghostState;
	}
	set ghostState(ghostState: GhostState) {
		this._ghostState = ghostState;
	}

	get currentDirection() {
		return this.lastDirection;
	}
	get level() {
		return this._level;
	}

}

Phaser.GameObjects.GameObjectFactory.register('ghost', function (
	this: Phaser.GameObjects.GameObjectFactory,
	pos: Required<Phaser.Types.Math.Vector2Like>,
	tint: Phaser.Display.Color,
	boardLayer: Phaser.Tilemaps.TilemapLayer,
	door: Phaser.GameObjects.Rectangle,
	level = 0
) {
	const ghost = new Ghost(this.scene, pos, tint, boardLayer, door, level);

	this.displayList.add(ghost);
	this.updateList.add(ghost);

	(this.scene.physics.world.enableBody(ghost, Phaser.Physics.Arcade.DYNAMIC_BODY).body as Phaser.Physics.Arcade.Body)
		.setOffset(-32, -32)
		.setFriction(0, 0);

	return ghost;
});

declare global {
	namespace Phaser.GameObjects {
		interface GameObjectFactory {
			ghost(
				pos: Phaser.Types.Math.Vector2Like,
				tint: Phaser.Display.Color,
				boardLayer: Phaser.Tilemaps.TilemapLayer,
				door: Phaser.GameObjects.Rectangle,
				level: number
			): Ghost
		}
	}
}
